import findIndex from 'lodash/findIndex';
import isEmpty from 'lodash/isEmpty';
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {DESTINATION_DATA} from 'proptypes';
import {connect} from 'react-redux';
import {withRouter} from 'react-router-dom';
import {compose} from 'redux';
import dayjs from 'utils/dayjs';
import StorageUtils from '@spothero/utils/storage';
import UrlUtils from '@spothero/utils/url';
import GTMUtils from 'utils/gtm';
import SHUtils from 'utils/sh-utils';
import withConfig from 'config/withConfig';
import {getSpots, getSelectedSpotById} from 'store/spots/spots-selectors';
import {setSelectedSpotId} from 'store/spots/spots-actions';
import {updateSelected} from 'store/spot/spot-actions';
import SearchUtils from 'utils/search-utils';
import {push, replace} from 'store/router/router-actions';
import trackSearchLanding from 'segment/pages/search-landing';
import trackAppleMapsExperience from 'segment/pages/apple-maps-experience';
import {
    setRecentlyViewed as setRecently,
    setSortType,
    toggleMobileListView,
} from 'store/search/search-actions';
import SearchMeta from 'seo/search-meta';
import {Helmet} from 'react-helmet-async';
import classNames from 'classnames';
import ExperimentUtils, {IMMEDIATE_AB_EXPERIMENTS} from 'utils/experiment';

export const VIEW = {
    TRANSIENT: 'transient',
};

// Event tray in mobile web is shown based on the variables below
const EVENTS_COUNT_GREATER_THAN_CONSTANT = 5;
const EVENTS_HAPPENING_IN_X_NEXT_DAYS = 30;
const MIN_EVENTS_TO_SHOW_CHANGE_EVENT_BUTTON = 2;

export const shouldShowEventsTrayInMobile = ({
    events,
    isEventListEnabled,
    timezone,
    eventsHappeningInXNextDays = EVENTS_HAPPENING_IN_X_NEXT_DAYS,
    eventsCountGreaterThanConstant = EVENTS_COUNT_GREATER_THAN_CONSTANT,
}) => {
    const TODAY = dayjs().tz(timezone);
    const MAX_LIMIT_DAY = dayjs()
        .tz(timezone)
        .add(eventsHappeningInXNextDays, 'day');

    return (
        isEventListEnabled &&
        (events.length > eventsCountGreaterThanConstant ||
            events.some(event =>
                dayjs(event.starts)
                    .tz(timezone)
                    .isBetween(TODAY, MAX_LIMIT_DAY, 'day', [])
            ))
    );
};

export default EnhancedComponent => {
    class WithSearch extends Component {
        static propTypes = {
            history: PropTypes.shape({
                location: PropTypes.object,
            }),
            match: PropTypes.shape({
                params: PropTypes.shape({
                    page: PropTypes.string,
                    section: PropTypes.string,
                }),
            }).isRequired,
            searchType: PropTypes.string.isRequired,
            siteUrl: PropTypes.string.isRequired,
            isMobile: PropTypes.bool.isRequired,
            isMonthly: PropTypes.bool.isRequired,
            starts: PropTypes.string,
            ends: PropTypes.string,
            citySlug: PropTypes.string.isRequired,
            cityMonthlyRelativeUrl: PropTypes.string,
            cityRelativeUrl: PropTypes.string,
            event: PropTypes.object,
            destination: PropTypes.shape(DESTINATION_DATA).isRequired,
            spots: PropTypes.arrayOf(PropTypes.object).isRequired,
            querySpotId: PropTypes.number,
            searchRequestId: PropTypes.string,
            searchUrl: PropTypes.string,
            updateSelectedSpotId: PropTypes.func.isRequired,
            setRecentlyViewed: PropTypes.func.isRequired,
            idealDistance: PropTypes.number,
            isFiltered: PropTypes.bool,
            referrerAffiliate: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.number,
            ]),
            sortBy: PropTypes.string.isRequired,
            isMobileListViewActive: PropTypes.bool.isRequired,
            routePush: PropTypes.func.isRequired,
            onChangeSort: PropTypes.func.isRequired,
            showMobileListView: PropTypes.func.isRequired,
            appleMapsPartner: PropTypes.bool.isRequired,
            timezone: PropTypes.string.isRequired,
        };

        constructor(props) {
            super(props);
            const {
                searchType,
                destination,
                querySpotId,
                timezone,
                history: {location},
            } = props;
            const isVenue = searchType === 'venue';
            const queryParams = UrlUtils.parseParams(location.search);
            const isComingFromMidfunnel = Boolean(queryParams.midfunnel);
            const isTransientView = queryParams.view === VIEW.TRANSIENT;

            const isEventListEnabled = Boolean(
                isVenue &&
                    destination.has_upcoming_events &&
                    !destination.hide_event_modal &&
                    !querySpotId &&
                    !isComingFromMidfunnel &&
                    !isTransientView
            );

            const showEventList = shouldShowEventsTrayInMobile({
                events: destination.events,
                isEventListEnabled,
                timezone,
            });

            const canChangeEvent =
                destination.events?.length >=
                MIN_EVENTS_TO_SHOW_CHANGE_EVENT_BUTTON;

            this.state = {
                isEventListEnabled,
                showEventList,
                canChangeEvent,
                bodyClasses: SHUtils.determineInitialLoadBodyClasses({
                    isSearchPage: true,
                    searchType,
                }),
            };
        }

        static getDerivedStateFromProps(nextProps, prevState) {
            const {searchType, destination} = nextProps;
            const {isEventListEnabled: prevIsEventListEnabled} = prevState;

            const bodyClasses = classNames(
                SHUtils.determineInitialLoadBodyClasses({
                    isSearchPage: true,
                    searchType,
                })
            );

            const isEventListEnabled =
                searchType === 'venue' &&
                destination.has_upcoming_events &&
                !destination.hide_event_modal
                    ? true
                    : searchType !== 'venue' ||
                      !destination.has_upcoming_events ||
                      destination.hide_event_modal
                    ? false
                    : prevIsEventListEnabled;

            return {bodyClasses, isEventListEnabled};
        }

        componentDidMount() {
            const {
                searchType,
                referrerAffiliate,
                isMobileListViewActive,
                showMobileListView,
                appleMapsPartner,
                isMobile,
            } = this.props;

            const destinationCategory =
                searchType === 'event' || searchType === 'venue'
                    ? 'Venues'
                    : null;

            // Want to scrap this line unsure if it would be safe but there are no more references to searchStartTime in the ConWeb
            StorageUtils.set('searchStartTime', dayjs().format());

            SearchUtils.setPreviousSearch();

            trackSearchLanding(referrerAffiliate, destinationCategory);

            if (appleMapsPartner && isMobile) {
                trackAppleMapsExperience({searchType});
            }

            this._trackPageView();

            // Optimizely Mobile Web [Map vs List Landing View] A/B Test implementation
            if (isMobileListViewActive) {
                const variationId = ExperimentUtils.getVariation(
                    IMMEDIATE_AB_EXPERIMENTS.MOBILE_WEB_SEARCH_MAP_LANDING
                );

                if (variationId !== 'a' && variationId === 'map_landing') {
                    showMobileListView(false);
                }
            }
        }

        componentDidUpdate(prevProps) {
            const {
                searchRequestId,
                querySpotId,
                updateSelectedSpotId,
                setRecentlyViewed,
            } = this.props;
            const {
                searchRequestId: prevSearchRequestId,
                querySpotId: prevQuerySpotId,
            } = prevProps;

            if (searchRequestId !== prevSearchRequestId) {
                this._trackPageView();
            }

            if (prevQuerySpotId && !querySpotId) {
                // spot-id was removed from URL, close spot details
                updateSelectedSpotId({
                    spotId: null,
                });
            } else if (prevQuerySpotId !== querySpotId) {
                // new spot-id was detected in URL, open that one instead
                setRecentlyViewed(querySpotId);
                updateSelectedSpotId({
                    spotId: querySpotId,
                });
            }
        }

        componentWillUnmount() {
            if (this._rateLoader) {
                this._rateLoader.cancel();
                this._rateLoader = null;
            }
        }

        _onToggleEventList = () => {
            this.setState(prevState => ({
                showEventList: !prevState.showEventList,
            }));
        };

        _onSpotDetailsHidden = () => {
            const {
                history: {
                    location: {pathname, search},
                },
                routePush,
            } = this.props;
            const queryParams = UrlUtils.parseParams(search);

            delete queryParams['spot-id'];

            routePush(
                `${pathname}?${UrlUtils.createQueryString(queryParams)}`,
                {reload: false}
            );
        };

        _onShowDetailsClick = spotId => {
            const {spots, sortBy} = this.props;

            StorageUtils.set(SearchUtils.SEGMENT_SPOT_META, {
                rank: findIndex(spots, {spotId}) + 1,
                sortBy,
            });
        };

        _trackPageView() {
            const {searchType} = this.props;

            GTMUtils.push({
                event: 'pageview',
                page: searchType === 'city' ? 'city' : 'search',
            });
        }

        render() {
            const {
                history,
                sortBy,
                searchType,
                searchUrl,
                isMonthly,
                starts,
                ends,
                spots,
                siteUrl,
                citySlug,
                cityMonthlyRelativeUrl,
                cityRelativeUrl,
                destination,
                event,
                querySpotId,
                routePush,
                idealDistance,
                isFiltered,
                onChangeSort,
            } = this.props;
            const {
                isEventListEnabled,
                showEventList,
                canChangeEvent,
                bodyClasses,
            } = this.state;
            const hasDestination = !isEmpty(destination);
            // This is redundant with the canonicalUrl calculation in SearchMeta
            // We will be address this with updates to meta content in CONWEB-483
            const canonicalUrl =
                searchType === 'event' && !isEmpty(event)
                    ? `${siteUrl}/${event.search_url}`
                    : hasDestination
                    ? `${siteUrl}/${citySlug}/${
                          isMonthly && destination.monthly_relative_url
                              ? destination.monthly_relative_url
                              : destination.relative_url
                      }?starts=${starts}&ends=${ends}`
                    : `${siteUrl}/${
                          isMonthly && cityMonthlyRelativeUrl
                              ? cityMonthlyRelativeUrl
                              : cityRelativeUrl
                      }?starts=${starts}&ends=${ends}`;

            const queryParams = UrlUtils.parseParams(history.location.search);
            const showFindFlexParkingMonthly = queryParams.findFlexPark;

            return (
                <>
                    <Helmet>
                        <body className={bodyClasses} />
                    </Helmet>
                    <SearchMeta />
                    <EnhancedComponent
                        history={history}
                        sortBy={sortBy}
                        searchType={searchType}
                        searchUrl={searchUrl}
                        canonicalUrl={canonicalUrl}
                        isMonthly={isMonthly}
                        starts={starts}
                        ends={ends}
                        spots={spots}
                        selectedSpotId={querySpotId}
                        pushTo={routePush}
                        onSpotDetailsHidden={this._onSpotDetailsHidden}
                        idealDistance={idealDistance}
                        isEventListEnabled={isEventListEnabled}
                        showEventList={showEventList}
                        isFiltered={isFiltered}
                        onChangeSort={onChangeSort}
                        onToggleEventList={this._onToggleEventList}
                        onShowDetailsClick={this._onShowDetailsClick}
                        canChangeEvent={canChangeEvent}
                        showFindFlexParkingMonthly={showFindFlexParkingMonthly}
                    />
                </>
            );
        }
    }

    const mapStateToProps = (state, ownProps) => {
        const {
            city: {
                data: {
                    id: cityId,
                    slug: citySlug,
                    timezone,
                    monthly_relative_url: cityMonthlyRelativeUrl,
                    relative_url: cityRelativeUrl,
                },
            },
            destination: {data: destination},
            event: {data: event},
            filters: {numActive},
            search: {
                data: {
                    searchType,
                    referrerAffiliate,
                    sortBy,
                    isMobileListViewActive,
                },
            },
            searchRequest: {
                monthly: isMonthly,
                starts,
                ends,
                latitude,
                longitude,
                eid: eventId,
                ideal_search_distance: idealDistance,
                id: searchRequestId,
                pageType,
                search_url: searchUrl,
                partner,
            },
        } = state;
        const {
            history: {
                location: {search: query},
            },
        } = ownProps;
        const {'spot-id': querySpotId} = UrlUtils.parseParams(query);

        return {
            isMonthly,
            starts,
            ends,
            eventId,
            cityId,
            citySlug,
            cityMonthlyRelativeUrl,
            cityRelativeUrl,
            timezone,
            searchRequestId,
            pageType,
            searchUrl,
            event,
            destination,
            searchType,
            idealDistance,
            querySpotId,
            latitude,
            longitude,
            referrerAffiliate,
            sortBy,
            isFiltered: numActive > 0,
            spots: getSpots(state),
            selectedSpot: getSelectedSpotById(state),
            isMobileListViewActive,
            appleMapsPartner: partner === 'apple' || partner === 'appleTest',
        };
    };

    const mapDispatchToProps = {
        setSelectedSpot: updateSelected,
        updateSelectedSpotId: setSelectedSpotId,
        setRecentlyViewed: setRecently,
        onChangeSort: setSortType,
        routePush: push,
        routeReplace: replace,
        showMobileListView: toggleMobileListView,
    };

    const enhance = compose(
        withConfig(['isMobile', 'siteUrl']),
        withRouter,
        connect(mapStateToProps, mapDispatchToProps)
    );

    return enhance(WithSearch);
};
